/**
 * Embudo de entrega REAL: enviado → entregado ✓✓ → leído ✓✓ azul → respondió.
 *
 * Todo sale de ACKs y recibos del transporte (Baileys) y del inbound real. Es la métrica
 * más honesta del producto: la lectura de WhatsApp es un recibo de verdad, a diferencia
 * del "open rate" de email (que necesita un pixel que no mandamos).
 *
 * Decisiones de forma:
 * - UNA serie, UN color. La longitud de la barra ya codifica la magnitud; pintar cada
 *   paso de un hue distinto la codificaría dos veces y quemaría el único canal libre.
 *   La identidad de cada fila la da su label, no su color.
 * - Denominador SIEMPRE visible ("X de Y"): un % suelto no dice sobre qué base está.
 * - Track un paso más claro del mismo hue; barras finas; sin bordes alrededor de la marca.
 */

import { Send, CheckCheck, MessageSquare, TrendingUp } from "lucide-react";
import type { TotalResultados } from "../types.real";
import { num, pct } from "../utils/format";

interface Props {
  total: Partial<TotalResultados> | null | undefined;
  /** true = todavía no se puede saber quién volvió (falta cargar la base del período siguiente) */
  atribucionPendiente?: boolean;
  /** true = estos números NO salieron de un transporte real (simulado / SMTP sin configurar) */
  simulado?: boolean;
}

export default function DeliveryFunnel({ total, atribucionPendiente, simulado }: Props) {
  const env = total?.enviados || 0;

  const filas = [
    { label: "Enviados", val: env, icon: Send, nota: "salieron de Activa" },
    { label: "Entregados", val: total?.entregados || 0, icon: CheckCheck, nota: "✓✓ llegaron al teléfono" },
    { label: "Leídos", val: total?.leidos || 0, icon: CheckCheck, nota: "✓✓ azul · sólo WhatsApp" },
    { label: "Respondieron", val: total?.respondieron || 0, icon: MessageSquare, nota: "contestaron el mensaje" },
  ];

  return (
    <section className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
      <header className="flex items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="text-base font-semibold text-slate-900 tracking-tight">Estado de entrega</h2>
          <p className="text-xs text-slate-500 mt-0.5">Qué pasó con cada mensaje después de salir</p>
        </div>
        {simulado ? (
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-lg bg-amber-100 text-amber-700 flex-shrink-0">Prueba</span>
        ) : (
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-lg bg-emerald-50 text-emerald-700 flex-shrink-0">Real</span>
        )}
      </header>

      <div className="space-y-4">
        {filas.map((f) => {
          const p = pct(f.val, env);
          const Icon = f.icon;
          return (
            <div key={f.label}>
              <div className="flex items-baseline justify-between gap-3 mb-1.5">
                <div className="flex items-center gap-2 min-w-0">
                  <Icon size={14} className="text-slate-400 flex-shrink-0" />
                  <span className="text-[13px] font-medium text-slate-700">{f.label}</span>
                  <span className="text-[11px] text-slate-500 truncate hidden sm:inline">· {f.nota}</span>
                </div>
                <div className="flex items-baseline gap-2 flex-shrink-0">
                  <span className="text-[15px] font-semibold text-slate-900">{num(f.val)}</span>
                  {/* el denominador explícito: sin el "de Y" el % no significa nada */}
                  {f.label !== "Enviados" && <span className="text-[11px] text-slate-400">de {num(env)} · {p}%</span>}
                </div>
              </div>
              {/* barra fina, misma marca para toda la serie, track un paso más claro */}
              <div className="h-2 rounded-full bg-violet-100/70 overflow-hidden">
                <div className="h-full rounded-full bg-violet-600 transition-all duration-700 ease-out" style={{ width: `${Math.max(1, p)}%` }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* "Volvieron" no es entrega: es atribución, y sólo existe con una base posterior cargada.
          Un 0 acá se leería como "nadie volvió" cuando la verdad es "todavía no lo medimos". */}
      <div className="mt-5 pt-5 border-t border-slate-100">
        <div className="flex items-baseline justify-between gap-3 mb-1.5">
          <div className="flex items-center gap-2">
            <TrendingUp size={14} className="text-emerald-500 flex-shrink-0" />
            <span className="text-[13px] font-medium text-slate-700">Volvieron a comprar</span>
          </div>
          {atribucionPendiente ? (
            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-slate-100 text-slate-500">Sin medir</span>
          ) : (
            <div className="flex items-baseline gap-2">
              <span className="text-[15px] font-semibold text-emerald-700">{num(total?.volvieron || 0)}</span>
              <span className="text-[11px] text-slate-400">de {num(env)} · {pct(total?.volvieron || 0, env)}%</span>
            </div>
          )}
        </div>
        <div className={`h-2 rounded-full overflow-hidden ${atribucionPendiente ? "bg-slate-50 border border-dashed border-slate-200" : "bg-emerald-100"}`}>
          {!atribucionPendiente && (
            <div className="h-full rounded-full bg-emerald-600 transition-all duration-700 ease-out" style={{ width: `${Math.max(1, pct(total?.volvieron || 0, env))}%` }} />
          )}
        </div>
      </div>

      <p className="text-[11px] text-slate-400 mt-5 leading-relaxed">
        El <strong className="text-sky-600">leído</strong> son los ✓✓ azules de WhatsApp. Si el destinatario desactivó la
        confirmación de lectura, cuenta como entregado pero no como leído. <strong className="text-slate-500">En email no
        podemos saber si lo abrieron</strong> — mandamos texto plano, sin rastreadores.
      </p>
    </section>
  );
}
